import { useQuery } from '@tanstack/react-query';
import {
  DEFAULT_TERMINOLOGY,
  SettingsApi,
  Terminology,
} from '@/services/school.api';
import { useAuthStore } from '@/stores/auth.store';
import { hasPlanFeature } from '@/lib/access';

/** What an LMS-only tenant sees in place of the unmodified default —
 * "level" only (Class → Batch); "group" stays Section/Sections (a batch
 * can still be subdivided). Applied purely client-side, never persisted —
 * see the isUnmodifiedDefault() guard below for why that's safe. */
const LMS_ONLY_DEFAULT_TERMINOLOGY: Terminology = {
  ...DEFAULT_TERMINOLOGY,
  level: 'Batch',
  levelPlural: 'Batches',
};

function isUnmodifiedDefault(term: Terminology): boolean {
  return (
    term.level === DEFAULT_TERMINOLOGY.level &&
    term.levelPlural === DEFAULT_TERMINOLOGY.levelPlural &&
    term.group === DEFAULT_TERMINOLOGY.group &&
    term.groupPlural === DEFAULT_TERMINOLOGY.groupPlural &&
    term.institutionType === DEFAULT_TERMINOLOGY.institutionType
  );
}

/**
 * School-configurable academic labels (Class/Grade/Semester · Section/Batch).
 * Cached app-wide; falls back to defaults while loading or on error.
 *
 * For a tenant whose plan lacks 'school_core' (LMS-only), the *default*
 * itself flips to Batch/Batches — purely client-side, zero backend/schema
 * change. This only applies when the backend's terminology settings are
 * still the raw, never-overridden default (mergeTerminology always returns
 * exactly DEFAULT_TERMINOLOGY until an admin explicitly saves an override —
 * see settings.service.ts), so a school tenant's explicit override (or any
 * tenant's, if one is ever saved) always wins unchanged.
 */
export function useTerminology(): Terminology {
  const schoolFeatures = useAuthStore((s) => s.schoolFeatures);
  const { data } = useQuery({
    queryKey: ['terminology'],
    queryFn: SettingsApi.getTerminology,
    staleTime: 5 * 60 * 1000,
    placeholderData: DEFAULT_TERMINOLOGY,
  });
  const term = data ?? DEFAULT_TERMINOLOGY;

  const hasSchoolCore = hasPlanFeature(schoolFeatures, 'school_core');
  if (!hasSchoolCore && isUnmodifiedDefault(term)) {
    return LMS_ONLY_DEFAULT_TERMINOLOGY;
  }
  return term;
}
